'use client';

import { useEffect, useState } from 'react';

/** Every fixed piece of the ECHO launcher a page has to keep clear of. */
const LAUNCHER_SELECTOR = '[data-echo-launcher]';

/**
 * Height in px a page should reserve at the end of its scroll container so its
 * last content can clear the floating ECHO launcher.
 *
 * The launcher (`EchoLauncher`, mounted once by `(dashboard)/layout.tsx` and
 * shown on EVERY route in it) is `position: fixed` bottom-right, so like the
 * mobile tab bar it OVERLAYS page content rather than shrinking it. Unlike the
 * tab bar it is more than one box: the face sits above the bar, and the
 * self-engagement bubble opens above the face when the creator taps it — which
 * is what covered the last saved card and the "+ Add New Card" button under it.
 *
 * So the reserve is the TALLEST currently-mounted piece, measured from the
 * viewport bottom to its top edge, exactly as {@link useBottomNavReserve}
 * measures the bar. Measured rather than hardcoded for the same reasons, and one
 * more of its own: the face sits at a different offset per platform
 * (`bottom-24` on Android, `bottom-16` elsewhere) and the bubble higher again,
 * so no single literal clears both on both.
 *
 * It shrinks back when the bubble is closed — the page's last row is where a
 * transient reserve costs least, and the alternative is permanently holding open
 * a bubble-sized hole that is empty most of the time (more so now the bubble
 * only opens on a tap).
 *
 * Returns 0 wherever no launcher is mounted (outside the dashboard, or a route
 * an admin row hid it on), so callers need no host-specific branching.
 *
 * `enabled: false` returns 0 and attaches NOTHING — no observer, no listeners.
 * The tree observer re-measures (a forced reflow) on every class/style change
 * on the page, so a caller that would discard the value at its current width
 * should switch it off rather than pay for it.
 */
export function useEchoLauncherReserve(enabled = true): number {
  const [reserve, setReserve] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const measure = () => {
      const viewportBottom = document.documentElement.clientHeight;
      const pieces = document.querySelectorAll<HTMLElement>(LAUNCHER_SELECTOR);
      let tallest = 0;
      for (const piece of pieces) {
        // `offsetHeight` 0 means `display: none` — the face is hidden behind the
        // full-screen mobile panel, which is its own overlay and reserves nothing.
        if (piece.offsetHeight === 0) continue;
        tallest = Math.max(tallest, viewportBottom - dockedTop(piece));
      }
      setReserve(Math.max(0, tallest));
    };

    measure();

    // The bubble arrives (and leaves) long after mount, and the face toggles
    // `hidden` with the panel — both are DOM changes rather than resizes, so the
    // observer watches the tree instead of polling for them.
    const observer = new MutationObserver(measure);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    // iOS doesn't fire `window.resize` for the soft keyboard; the visual
    // viewport is the only signal there (same reason as the bottom-nav hook).
    window.visualViewport?.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, [enabled]);

  return enabled ? reserve : 0;
}

/**
 * A piece's top edge where it DOCKS, ignoring the creator's drag offset
 * (`useDraggableLauncher` applies it as the CSS `translate` property).
 *
 * A head parked up the screen sits over the middle of the page, where no
 * bottom padding can clear it — measuring it there turned the reserve into
 * nearly the whole viewport, an empty band below every list on scroll.
 */
function dockedTop(piece: HTMLElement): number {
  const { top } = piece.getBoundingClientRect();
  const translate = getComputedStyle(piece).translate;
  if (translate === 'none' || translate === '') return top;
  const [, y = '0'] = translate.split(' ');
  return top - (Number.parseFloat(y) || 0);
}

/** Breathing room between a page's last row and the launcher above it. */
const ECHO_LAUNCHER_CLEARANCE_PX = 16;

/**
 * How far above the viewport bottom a page's last content has to sit to clear
 * the floating ECHO launcher, in px — `undefined` where none is mounted, so the
 * page's own Tailwind padding stays in charge.
 *
 * A page under `(dashboard)` that anchors content to the bottom of its scroller
 * wants the LARGER of this and `useBottomNavClearance()`: the two overlap (the
 * launcher floats above the bar), so reserving both would double-count.
 */
export function useEchoLauncherClearance(enabled = true): number | undefined {
  const reserve = useEchoLauncherReserve(enabled);
  if (reserve === 0) return undefined;
  return reserve + ECHO_LAUNCHER_CLEARANCE_PX;
}
